import React, { useState, useEffect } from 'react';
import { 
  X, 
  Download, 
  Eye, 
  MessageSquare, 
  ChevronRight, 
  ChevronDown, 
  User, 
  Stethoscope, 
  Shield, 
  Building, 
  DollarSign, 
  FileText, 
  Phone, 
  Mail, 
  MapPin,
  Edit,
  Save,
  Loader,
  HelpCircle
} from 'lucide-react';
import DiscussionComponent from '../../component/application/DiscussionComponent';
import { updatePolicyInformation } from '../../services/applicationService';
import { getHospitalTariffDocuments } from '../../services/hospitalService';

// Helper to force download (restored after refactor)
const downloadFile = async (url, filename) => {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const contentType = response.headers.get('content-type') || '';
    const blob = await response.blob();
    let finalName = filename || 'document';
    // Infer extension if missing
    if (!/\.[a-zA-Z0-9]{2,5}$/.test(finalName)) {
      const extMap = {
        'application/pdf': '.pdf',
        'image/jpeg': '.jpg',
        'image/jpg': '.jpg',
        'image/png': '.png',
        'image/webp': '.webp'
      };
      const ext = extMap[contentType];
      if (ext) finalName += ext;
    }
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = finalName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(blobUrl);
  } catch (err) {
    console.error('Download failed, opening in new tab:', err);
    window.open(url, '_blank');
  }
};

const ApplicationRightPanel = ({
  // Document panel props
  showDocumentPanel,
  selectedDocument,
  onCloseDocumentPanel,
  onOpenDocument,
  
  // Discussion panel props
  showDiscussionPanel,
  onCloseDiscussionPanel,
  onOpenDiscussionPanel,
  applicationId,
  onUnreadCountChange,
  hasPendingClarification,
  unreadCount,
  
  // Application data
  application,
  
  // Collapsible sections
  collapsedSections,
  onToggleSection,
  
  // Utility functions
  formatAmount,
  formatDate,
  showPolicyEdit,
  onPolicyUpdated,

  // Document query props
  documentQueries = {},
  onOpenQueryModal,
  userTeam = 'policy'
}) => {
  // Policy edit state and functions
  const [showPolicyEditModal, setShowPolicyEditModal] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(false);
  const [editedPolicyData, setEditedPolicyData] = useState({});
  // Document viewer zoom (must be declared unconditionally)
  const [docZoom, setDocZoom] = useState(1);
  useEffect(() => {
    if (showDocumentPanel) setDocZoom(1);
  }, [showDocumentPanel, selectedDocument]);

  // Initialize policy data for editing
  const initializePolicyEdit = () => {
    setEditedPolicyData({
      policyNumber: application.policy?.number || '',
      insuranceCompany: application.policy?.insuranceCompany || '',
      policyType: application.policy?.type || '',
      coverageAmount: application.policy?.coverageAmount || '',
      startDate: application.policy?.startDate || '',
      endDate: application.policy?.endDate || '',
      tpaName: application.policy?.tpaName || '',
      employmentStatus: application.policy?.employmentStatus || '',
      workingCompanyName: application.policy?.workingCompanyName || '',
      monthlyIncome: application.policy?.monthlyIncome || '',
      designation: application.policy?.designation || '',
      natureOfWork: application.policy?.natureOfWork || ''
    });
    setShowPolicyEditModal(true);
  };

  // Handle policy data changes
  const handlePolicyChange = (field, value) => {
    setEditedPolicyData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Save policy changes
  const savePolicyChanges = async () => {
    setEditingPolicy(true);
    try {
      // Persist to Firestore
      const result = await updatePolicyInformation(
        applicationId,
        {
          policyNumber: editedPolicyData.policyNumber,
          insuranceCompany: editedPolicyData.insuranceCompany,
          policyType: editedPolicyData.policyType,
          coverageAmount: editedPolicyData.coverageAmount,
          startDate: editedPolicyData.startDate,
          endDate: editedPolicyData.endDate,
          tpaName: editedPolicyData.tpaName
        },
        {
          employmentStatus: editedPolicyData.employmentStatus,
          workingCompanyName: editedPolicyData.workingCompanyName,
          monthlyIncome: editedPolicyData.monthlyIncome,
          designation: editedPolicyData.designation,
          natureOfWork: editedPolicyData.natureOfWork
        }
      );

      if (!result.success) {
        throw new Error(result.error || 'Failed to update');
      }

      setShowPolicyEditModal(false);
      // Trigger a reload of application data in parent, if provided
      if (typeof onPolicyUpdated === 'function') {
        onPolicyUpdated();
      }
    } catch (error) {
      console.error('Error saving policy changes:', error);
      alert('Failed to save policy changes. Please try again.');
    } finally {
      setEditingPolicy(false);
    }
  };

  // Insurance companies list
  const insuranceCompanies = [
    "Bajaj Allianz General Insurance Co. Ltd",
    "Cholamandalam MS General Insurance Co. Ltd",
    "Navi General Insurance Ltd.",
    "Future Generali India Insurance Co. Ltd.",
    "HDFC ERGO General Insurance Co. Ltd",
    "IFFCO-TOKIO General Insurance Co. Ltd.",
    "ICICI Lombard General Insurance Co. Ltd",
    "Kotak Mahindra General insurance co. Ltd.",
    "Liberty General Insurance Co. Ltd.",
    "Magma HDI General Insurance Co. Ltd.",
    "National Insurance Co. Ltd.",
    "Raheja QBE General Insurance Co. Ltd.",
    "Reliance General Insurance Co. Ltd.",
    "Royal Sundaram General Insurance Co. Ltd",
    "SBI General Insurance Co. Ltd.",
    "Shriram General Insurance Co. Ltd.",
    "Tata-AIG General Insurance Co. Ltd.",
    "The New India Assurance Co. Ltd.",
    "The Oriental Insurance Co. Ltd.",
    "Universal Sompo General Insurance Co. Ltd.",
    "United India Insurance Co. Ltd.",
    "Acko General Insurance Ltd.",
    "Edelweiss General Insurance Company Limited",
    "Go Digit General Insurance Limited",
    "Aditya Birla Health insurance Co Ltd.",
    "Manipal Cigna Health Insurance Company Limited",
    "Niva bupa health insurance company limited",
    "Care Health Insurance Ltd",
    "Star Health & Allied Insurance Co. Ltd",
  ];

  const tpaCompanies = [
    "In-House",
    "Medi Assist Insurance TPA Private Limited",
    "MDIndia Health Insurance TPA Private Limited",
    "Paramount Health Services & Insurance TPA Private Limited",
    "Heritage Health Insurance TPA Private Limited",
    "Family Health Plan Insurance TPA Limited",
    "Raksha Health Insurance TPA Private Limited",
    "Vidal Health Insurance TPA Private Limited",
    "Volo Health Insurance TPA Pvt. Ltd ( Formerly East West Assist Insurance TPA Private Limited)",
    "Medsave Health Insurance TPA Limited",
    "Genins India Insurance TPA Limited",
    "Health India Insurance TPA Services Private Limited",
    "Good Health Insurance TPA Limited",
    "Park Mediclaim Insurance TPA Private Limited",
    "Safeway Insurance TPA Private Limited",
    "Ericson Insurance TPA Private Limited",
    "Health Insurance TPA of India Limited",
    "Vision Digital Insurance TPA Private Limited",
    "Link-K Insurance TPA Private Limited",
    "AKNA Health Insurance TPA Private Limited",
  ];
  if (showDocumentPanel && selectedDocument) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col h-full">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 truncate">
              Document Viewer
            </h3>
            <p className="text-sm text-gray-600 truncate">
              {selectedDocument.fileName}
            </p>
          </div>
          <div className="flex items-center space-x-2 ml-4">
            {(() => {
              const name = String(selectedDocument.fileName || '');
              const url = String(selectedDocument.url || '');
              const lowerName = name.toLowerCase();
              const lowerUrl = url.split('?')[0].toLowerCase();
              const isPdf = lowerName.endsWith('.pdf') || lowerUrl.endsWith('.pdf');
              const isImgByName = ['.jpg','.jpeg','.png','.gif','.webp'].some(ext => lowerName.endsWith(ext) || lowerUrl.endsWith(ext));
              const isImgByType = /image\//.test(selectedDocument.contentType || '');
              return isPdf || isImgByName || isImgByType;
            })() && (
              <div className="flex items-center space-x-1 mr-2">
                <button onClick={() => setDocZoom((z) => Math.max(0.25, Math.round((z - 0.1) * 100) / 100))} className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-100">-</button>
                <span className="w-12 text-center text-xs">{Math.round(docZoom * 100)}%</span>
                <button onClick={() => setDocZoom((z) => Math.min(4, Math.round((z + 0.1) * 100) / 100))} className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-100">+</button>
                <button onClick={() => setDocZoom(1)} className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-100">Fit</button>
              </div>
            )}
            <button
              onClick={() => window.open(selectedDocument.url, '_blank')}
              className="px-3 py-2 border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 flex items-center space-x-2 text-sm"
              title="Open in new tab"
            >
              <Eye className="h-4 w-4" />
              <span>Open</span>
            </button>
            <button
              onClick={() => {
                downloadFile(selectedDocument.url, selectedDocument.fileName);
              }}
              className="px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center space-x-2 text-sm"
            >
              <Download className="h-4 w-4" />
              <span>Download</span>
            </button>
            <button
              onClick={onCloseDocumentPanel}
              className="text-gray-400 hover:text-gray-600 p-1"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 p-4 overflow-hidden min-h-0">
          {(() => {
            const name = String(selectedDocument.fileName || '');
            const url = String(selectedDocument.url || '');
            const lowerName = name.toLowerCase();
            const lowerUrl = url.split('?')[0].toLowerCase();
            const isPdf = lowerName.endsWith('.pdf') || lowerUrl.endsWith('.pdf');
            return isPdf;
          })() ? (
            <iframe
              src={`${selectedDocument.url}#zoom=${Math.round(docZoom*100)}`}
              className="w-full h-full border border-gray-300 rounded"
              title="Document Viewer"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-50 rounded overflow-hidden">
              <img
                src={selectedDocument.url}
                alt={selectedDocument.fileName}
                style={{ transform: `scale(${docZoom})`, transformOrigin: 'center center' }}
                className="max-w-full max-h-full object-contain"
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  if (showDiscussionPanel) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col h-full">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50 flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-100 p-2 rounded-lg">
              <MessageSquare className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Policy Discussion
              </h3>
              <p className="text-sm text-gray-600">
                {hasPendingClarification 
                  ? "⚠️ Pending clarification required before approval" 
                  : "Communicate with the application team"
                }
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {hasPendingClarification && (
              <span className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded-full font-medium">
                Info Required
              </span>
            )}
            <button
              onClick={onCloseDiscussionPanel}
              className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-white transition-colors"
              title="Close Discussion Panel"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-hidden min-h-0">
          <div className="h-full p-2">
            <DiscussionComponent
              applicationId={applicationId}
              onUnreadCountChange={onUnreadCountChange}
              compact={true}
              hasPendingClarification={hasPendingClarification}
              style={{
                height: '100%',
                maxHeight: 'calc(100vh - 300px)'
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  // Application Overview Sections with Discussion Toggle
  return (
    <div className="space-y-4">
      {/* Discussion Toggle Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <button
          onClick={onOpenDiscussionPanel}
          className={`w-full flex items-center justify-between p-4 text-left transition-all duration-200 rounded-xl ${
            hasPendingClarification 
              ? "hover:bg-gradient-to-r hover:from-orange-50 hover:to-red-50 border-orange-200" 
              : "hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50"
          }`}
        >
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-lg ${
              hasPendingClarification ? "bg-orange-100" : "bg-blue-100"
            }`}>
              <MessageSquare className={`h-4 w-4 ${
                hasPendingClarification ? "text-orange-600" : "text-blue-600"
              }`} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Policy Discussion
                {hasPendingClarification && (
                  <span className="ml-2 text-orange-600 text-sm">⚠️</span>
                )}
              </h3>
              <p className="text-sm text-gray-600">
                {hasPendingClarification 
                  ? "Clarification required - click to respond" 
                  : "Click to open communication panel"
                }
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {hasPendingClarification && (
              <span className="bg-orange-500 text-white text-xs rounded-full px-2 py-1 min-w-[60px] text-center font-medium">
                Required
              </span>
            )}
            {unreadCount > 0 && (
              <span className="bg-red-500 text-white text-xs rounded-full px-2 py-1 min-w-[20px] text-center">
                {unreadCount}
              </span>
            )}
            <ChevronRight className="h-5 w-5 text-gray-400" />
          </div>
        </button>
      </div>

      {/* Collapsible Application Overview */}
      <CollapsibleApplicationOverview 
        application={application}
        collapsedSections={collapsedSections}
        onToggleSection={onToggleSection}
        onOpenDocument={onOpenDocument}
        formatAmount={formatAmount}
        formatDate={formatDate}
        initializePolicyEdit={initializePolicyEdit}
        showPolicyEdit={showPolicyEdit}
        documentQueries={documentQueries}
        onOpenQueryModal={onOpenQueryModal}
      />

      {/* Policy Edit Modal */}
      {showPolicyEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Edit Policy Information</h2>
              <button
                onClick={() => setShowPolicyEditModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Basic Policy Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Policy Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Policy Number *
                    </label>
                    <input
                      type="text"
                      value={editedPolicyData.policyNumber}
                      onChange={(e) => handlePolicyChange('policyNumber', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter policy number"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Insurance Company *
                    </label>
                    <select
                      value={editedPolicyData.insuranceCompany}
                      onChange={(e) => handlePolicyChange('insuranceCompany', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select Insurance Company</option>
                      {insuranceCompanies.map((company) => (
                        <option key={company} value={company}>
                          {company}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Policy Type *
                    </label>
                    <select
                      value={editedPolicyData.policyType}
                      onChange={(e) => handlePolicyChange('policyType', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select Policy Type</option>
                      <option value="individual">Individual</option>
                      <option value="corporate">Corporate</option>
                      <option value="family">Family</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Coverage Amount *
                    </label>
                    <input
                      type="number"
                      value={editedPolicyData.coverageAmount}
                      onChange={(e) => handlePolicyChange('coverageAmount', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter coverage amount"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Policy Start Date *
                    </label>
                    <input
                      type="date"
                      value={editedPolicyData.startDate}
                      onChange={(e) => handlePolicyChange('startDate', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Policy End Date *
                    </label>
                    <input
                      type="date"
                      value={editedPolicyData.endDate}
                      onChange={(e) => handlePolicyChange('endDate', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      TPA Name
                    </label>
                    <select
                      value={editedPolicyData.tpaName}
                      onChange={(e) => handlePolicyChange('tpaName', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select TPA</option>
                      {tpaCompanies.map((tpa) => (
                        <option key={tpa} value={tpa}>
                          {tpa}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Employment Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Employment Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Employment Status
                    </label>
                    <select
                      value={editedPolicyData.employmentStatus}
                      onChange={(e) => handlePolicyChange('employmentStatus', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select Employment Status</option>
                      <option value="employed">Employed</option>
                      <option value="self-employed">Self Employed</option>
                      <option value="unemployed">Unemployed</option>
                      <option value="student">Student</option>
                      <option value="homemaker">Homemaker</option>
                      <option value="retired">Retired</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Monthly Income
                    </label>
                    <input
                      type="number"
                      value={editedPolicyData.monthlyIncome}
                      onChange={(e) => handlePolicyChange('monthlyIncome', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter monthly income"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Company Name
                    </label>
                    <input
                      type="text"
                      value={editedPolicyData.workingCompanyName}
                      onChange={(e) => handlePolicyChange('workingCompanyName', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter company name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Designation
                    </label>
                    <input
                      type="text"
                      value={editedPolicyData.designation}
                      onChange={(e) => handlePolicyChange('designation', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter designation"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nature of Work
                    </label>
                    <input
                      type="text"
                      value={editedPolicyData.natureOfWork}
                      onChange={(e) => handlePolicyChange('natureOfWork', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter nature of work"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                onClick={() => setShowPolicyEditModal(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={savePolicyChanges}
                disabled={editingPolicy}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 transition-colors"
              >
                {editingPolicy ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    <span>Save Changes</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Separate component for the collapsible sections
const CollapsibleApplicationOverview = ({ 
  application, 
  collapsedSections, 
  onToggleSection, 
  onOpenDocument,
  formatAmount,
  formatDate,
  initializePolicyEdit,
  showPolicyEdit,
  documentQueries = {},
  onOpenQueryModal
}) => (
  <div className="space-y-4">
    {/* Patient Information Section */}
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      <button
        onClick={() => onToggleSection('patient')}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center space-x-3">
          <div className="bg-teal-100 p-2 rounded-lg">
            <User className="h-4 w-4 text-teal-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Patient Information</h3>
        </div>
        {collapsedSections.patient ? 
          <ChevronRight className="h-5 w-5 text-gray-400" /> : 
          <ChevronDown className="h-5 w-5 text-gray-400" />
        }
      </button>
      
      {!collapsedSections.patient && (
        <div className="px-4 pb-4 border-t border-gray-100">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="text-sm font-medium text-gray-500">Patient Name</label>
              <p className="text-gray-900 font-semibold">{application.patient?.name || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Age & Gender</label>
              <p className="text-gray-900">{application.patient?.age || 'N/A'} years, {application.patient?.gender || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Contact</label>
              <div className="space-y-1">
                <div className="flex items-center space-x-2">
                  <Phone className="h-3 w-3 text-gray-400" />
                  <p className="text-sm text-gray-900">{application.patient?.phone || 'N/A'}</p>
                </div>
                {application.patient?.email && (
                  <div className="flex items-center space-x-2">
                    <Mail className="h-3 w-3 text-gray-400" />
                    <p className="text-sm text-gray-900">{application.patient.email}</p>
                  </div>
                )}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Address</label>
              <p className="text-gray-900 text-sm">{application.patient?.address || 'N/A'}</p>
            </div>
          </div>
        </div>
      )}
    </div>

    {/* Medical Information Section */}
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      <button
        onClick={() => onToggleSection('medical')}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center space-x-3">
          <div className="bg-blue-100 p-2 rounded-lg">
            <Stethoscope className="h-4 w-4 text-blue-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Medical Information</h3>
        </div>
        {collapsedSections.medical ? 
          <ChevronRight className="h-5 w-5 text-gray-400" /> : 
          <ChevronDown className="h-5 w-5 text-gray-400" />
        }
      </button>
      
      {!collapsedSections.medical && (
        <div className="px-4 pb-4 border-t border-gray-100">
          <div className="space-y-4 mt-4">
            
            {/* Basic Medical Information */}
            {application.medical?.diagnosis && (
              <div>
                <label className="text-sm font-medium text-gray-500">Diagnosis</label>
                <p className="text-gray-900 bg-blue-50 p-2 rounded">{application.medical.diagnosis}</p>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              {application.medical?.treatmentType && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Treatment Type</label>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    application.medical?.treatmentType === "surgical"
                      ? "bg-red-100 text-red-800"
                      : "bg-blue-100 text-blue-800"
                  }`}>
                    {application.medical?.treatmentType?.charAt(0).toUpperCase() +
                      application.medical?.treatmentType?.slice(1)}
                  </span>
                </div>
              )}
              {application.medical?.estimatedCost && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Estimated Cost</label>
                  <p className="text-lg font-semibold text-gray-900">
                    {formatAmount(application.medical?.estimatedCost)}
                  </p>
                </div>
              )}
            </div>

            {/* Payment / Financing Preference */}
            <div className="mt-2">
              <label className="text-sm font-medium text-gray-500">Payment Preference</label>
              <div className="mt-1">
                <span
                  className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full border ${
                    application.paymentPreference === 'pdc'
                      ? 'bg-amber-100 text-amber-800 border-amber-200'
                      : 'bg-indigo-100 text-indigo-800 border-indigo-200'
                  }`}
                  title={application.paymentPreference === 'pdc' ? 'Post Dated Cheques route' : 'Loan workflow route'}
                >
                  {application.paymentPreference === 'pdc' ? 'PDC (Post Dated Cheques)' : 'Loan'}
                </span>
              </div>
            </div>
            
            {application.medical?.symptoms && (
              <div>
                <label className="text-sm font-medium text-gray-500">Symptoms</label>
                <p className="text-gray-900 text-sm">{application.medical.symptoms}</p>
              </div>
            )}
            
            {application.medical?.duration && (
              <div>
                <label className="text-sm font-medium text-gray-500">Duration</label>
                <p className="text-gray-900 text-sm">{application.medical.duration}</p>
              </div>
            )}

            {/* Treatments Section - Enhanced from DoctorReview logic */}
            {application?.medical?.treatments && application.medical.treatments.length > 0 && (
              <div className="mt-6">
                <h6 className="font-medium text-gray-700 mb-3">
                  Treatment Details
                </h6>
                <div className="space-y-3">
                  {application.medical.treatments.map((treatment) => (
                    <div
                      key={treatment.id}
                      className="bg-gray-50 rounded-lg p-3 border border-gray-200"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <h6 className="font-medium text-gray-800 mb-1">
                            {treatment.diagnosisName || treatment.diagnosis || 'Diagnosis not specified'}
                          </h6>
                          
                          {/* ICD Code if available */}
                          {treatment.icdCode && (
                            <p className="text-sm text-gray-600 mb-1">
                              <span className="font-medium">ICD Code:</span> {treatment.icdCode}
                              {treatment.icdDescription && (
                                <span> - {treatment.icdDescription}</span>
                              )}
                            </p>
                          )}
                          
                          {/* Treatment Type */}
                          {treatment.treatmentType && (
                            <p className="text-sm text-gray-600">
                              <span className="font-medium">Type:</span> {treatment.treatmentType}
                            </p>
                          )}
                          
                          {/* Procedure Name */}
                          {treatment.procedureName && (
                            <p className="text-sm text-gray-600">
                              <span className="font-medium">Procedure:</span> {treatment.procedureName}
                            </p>
                          )}
                          
                          {/* Description */}
                          {treatment.description && (
                            <p className="text-sm text-gray-600 mt-1">
                              <span className="font-medium">Description:</span> {treatment.description}
                            </p>
                          )}
                          
                          {/* Cost if available */}
                          {treatment.estimatedCost && (
                            <p className="text-sm text-gray-600">
                              <span className="font-medium">Estimated Cost:</span> {formatAmount(treatment.estimatedCost)}
                            </p>
                          )}
                          
                          {/* Review Information */}
                          {treatment.reviewedBy && (
                            <p className="text-xs text-gray-500 mt-2">
                              Last reviewed by {treatment.reviewerName} on{" "}
                              {new Date(treatment.reviewedAt?.seconds * 1000 || treatment.reviewedAt).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      {/* Treatment Documents */}
                      {application?.documents?.treatmentDocs?.[treatment.id] && (
                        <div className="border-t border-gray-200 pt-2 mt-2">
                          <p className="text-xs text-gray-500 mb-1">Treatment Document:</p>
                          <p className="text-sm text-blue-600">
                            {application.documents.treatmentDocs[treatment.id].name}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* If no treatments but has medical data, show fallback */}
            {(!application?.medical?.treatments || application.medical.treatments.length === 0) && 
             (application.medical?.diagnosis || application.medical?.treatmentType) && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">
                  Basic medical information available. Detailed treatment data not yet provided.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>

    {/* Policy Information Section */}
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      <button
        onClick={() => onToggleSection('policy')}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center space-x-3">
          <div className="bg-green-100 p-2 rounded-lg">
            <Shield className="h-4 w-4 text-green-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Policy Information</h3>
        </div>
        <div className="flex items-center space-x-2">
          {showPolicyEdit && (<button
            onClick={(e) => {
              e.stopPropagation();
              initializePolicyEdit();
            }}
            className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50 transition-colors"
            title="Edit Policy Information"
          >
            <Edit className="h-4 w-4" />
          </button>)}
          {collapsedSections.policy ? 
            <ChevronRight className="h-5 w-5 text-gray-400" /> : 
            <ChevronDown className="h-5 w-5 text-gray-400" />
          }
        </div>
      </button>
      
      {!collapsedSections.policy && (
        <div className="px-4 pb-4 border-t border-gray-100">
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Policy Number</label>
                <p className="text-gray-900 font-mono text-sm bg-gray-50 p-2 rounded">
                  {application.policy?.number}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Insurance Company</label>
                <p className="text-gray-900 bg-gray-50 p-2 rounded">
                  {application.policy?.insuranceCompany}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Policy Type</label>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  application.policy?.type === "corporate"
                    ? "bg-purple-100 text-purple-800"
                    : "bg-blue-100 text-blue-800"
                }`}>
                  {application.policy?.type?.charAt(0).toUpperCase() +
                    application.policy?.type?.slice(1)}
                </span>
              </div>
              {application.policy?.tpaName && (
                <div>
                  <label className="text-sm font-medium text-gray-500">TPA</label>
                  <p className="text-gray-900 bg-gray-50 p-2 rounded">
                    {application.policy.tpaName}
                  </p>
                </div>
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Policy Period</label>
              <p className="text-gray-900 bg-gray-50 p-2 rounded">
                {application.policy?.startDate} to {application.policy?.endDate}
              </p>
            </div>

            {/* Policy Holder Details */}
            {(application.policyHolder || application.patient) && (
              <div className="border-t border-gray-100 pt-4">
                <h4 className="text-sm font-semibold text-gray-800 mb-2">Policy Holder</h4>
                {application.policyHolder ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${
                        application.policyHolder.isSelf ? "bg-teal-100 text-teal-800" : "bg-gray-100 text-gray-800"
                      }`}>
                        {application.policyHolder.isSelf ? "Self" : (application.policyHolder.relationshipWithPatient || "Related")}
                      </span>
                      {!application.policyHolder.isSelf && application.policyHolder.relationshipWithPatient && (
                        <span className="text-xs text-gray-600">({application.policyHolder.relationshipWithPatient})</span>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-500">Name</label>
                        <p className="text-gray-900">{application.policyHolder.name || application.patient?.name || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Age & Gender</label>
                        <p className="text-gray-900">{(application.policyHolder.age ?? application.patient?.age) || 'N/A'}{typeof (application.policyHolder.age ?? application.patient?.age) !== 'undefined' ? ' years' : ''}{application.policyHolder.gender || application.patient?.gender ? `, ${application.policyHolder.gender || application.patient?.gender}` : ''}</p>
                      </div>
                      {application.policyHolder?.dob && (
                        <div>
                          <label className="text-sm font-medium text-gray-500">Policy Holder DOB</label>
                          <p className="text-gray-900">{application.policyHolder.dob}</p>
                        </div>
                      )}
                      {(application.policyHolder.phone || application.policyHolder.email) && (
                        <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                          {application.policyHolder.phone && (
                            <div>
                              <label className="text-sm font-medium text-gray-500">Phone</label>
                              <p className="text-gray-900">{application.policyHolder.phone}</p>
                            </div>
                          )}
                          {application.policyHolder.email && (
                            <div>
                              <label className="text-sm font-medium text-gray-500">Email</label>
                              <p className="text-gray-900">{application.policyHolder.email}</p>
                            </div>
                          )}
                          {application.policyHolder.alternatePhone && (
                            <div>
                              <label className="text-sm font-medium text-gray-500">Alternate Mobile</label>
                              <p className="text-gray-900">{application.policyHolder.alternatePhone}</p>
                            </div>
                          )}
                        </div>
                      )}
                      {application.policyHolder.address && (
                        <div className="md:col-span-2">
                          <label className="text-sm font-medium text-gray-500">Address</label>
                          <p className="text-gray-900 text-sm">{application.policyHolder.address}</p>
                        </div>
                      )}
                    </div>

                    {/* Policy Holder KYC */}
                    {(application.policyHolder.kyc?.aadharNumber || application.policyHolder.kyc?.panNumber) && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {application.policyHolder.kyc?.aadharNumber && (
                          <div>
                            <label className="text-sm font-medium text-gray-500">Aadhar</label>
                            <p className="text-gray-900 font-mono text-sm bg-gray-50 p-2 rounded">{application.policyHolder.kyc.aadharNumber}</p>
                          </div>
                        )}
                        {application.policyHolder.kyc?.panNumber && (
                          <div>
                            <label className="text-sm font-medium text-gray-500">PAN</label>
                            <p className="text-gray-900 font-mono text-sm bg-gray-50 p-2 rounded">{application.policyHolder.kyc.panNumber}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Nominee Details */}
                    {(application.policyHolder.nominee?.name || application.policyHolder.nominee?.relationshipWithNominee || application.policyHolder.nominee?.contact) && (
                      <div className="border-t border-gray-100 pt-3">
                        <h5 className="text-sm font-medium text-gray-700 mb-2">Nominee</h5>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {application.policyHolder.nominee?.name && (
                            <div>
                              <label className="text-sm font-medium text-gray-500">Name</label>
                              <p className="text-gray-900">{application.policyHolder.nominee.name}</p>
                            </div>
                          )}
                          {application.policyHolder.nominee?.relationshipWithNominee && (
                            <div>
                              <label className="text-sm font-medium text-gray-500">Relationship</label>
                              <p className="text-gray-900">{application.policyHolder.nominee.relationshipWithNominee}</p>
                            </div>
                          )}
                          {application.policyHolder.nominee?.contact && (
                            <div>
                              <label className="text-sm font-medium text-gray-500">Contact</label>
                              <p className="text-gray-900">{application.policyHolder.nominee.contact}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-600">Not available</p>
                )}
              </div>
            )}

            {/* Employment Information (from Policy Holder step) */}
            {application.employment && (
              <div className="border-t border-gray-100 pt-4">
                <h4 className="text-sm font-semibold text-gray-800 mb-2">Employment Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {application.employment.status && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Status</label>
                      <p className="text-gray-900">{application.employment.status}</p>
                    </div>
                  )}
                  {application.employment.workingCompanyName && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Company</label>
                      <p className="text-gray-900">{application.employment.workingCompanyName}</p>
                    </div>
                  )}
                  {(application.employment.designation || application.employment.natureOfWork) && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Role</label>
                      <p className="text-gray-900">{application.employment.designation || application.employment.natureOfWork}</p>
                    </div>
                  )}
                  {typeof application.employment.monthlyIncome === 'number' && application.employment.monthlyIncome > 0 && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Monthly Income</label>
                      <p className="text-gray-900">{formatAmount(application.employment.monthlyIncome)}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>

    {/* Hospital Information Section */}
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      <button
        onClick={() => onToggleSection('hospital')}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center space-x-3">
          <div className="bg-orange-100 p-2 rounded-lg">
            <Building className="h-4 w-4 text-orange-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Hospital Information</h3>
        </div>
        {collapsedSections.hospital ? 
          <ChevronRight className="h-5 w-5 text-gray-400" /> : 
          <ChevronDown className="h-5 w-5 text-gray-400" />
        }
      </button>
      
      {!collapsedSections.hospital && (
        <div className="px-4 pb-4 border-t border-gray-100">
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-gray-500">Hospital Name</label>
              <p className="text-gray-900 font-semibold">{application.hospital?.name || application.hospitalName || 'N/A'}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Location</label>
                <div className="flex items-center space-x-2">
                  <MapPin className="h-3 w-3 text-gray-400" />
                  <p className="text-sm text-gray-900">{application.hospital?.location || 'N/A'}</p>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Network</label>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  application.hospital?.isNetworkHospital
                    ? "bg-green-100 text-green-800"
                    : "bg-yellow-100 text-yellow-800"
                }`}>
                  {application.hospital?.isNetworkHospital ? "Network" : "Non-Network"}
                </span>
              </div>
            </div>
            {application.hospital?.doctor && (
              <div>
                <label className="text-sm font-medium text-gray-500">Attending Doctor</label>
                <p className="text-gray-900">{application.hospital.doctor}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>

    {/* Financial Information Section */}
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      <button
        onClick={() => onToggleSection('financial')}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center space-x-3">
          <div className="bg-purple-100 p-2 rounded-lg">
            <DollarSign className="h-4 w-4 text-purple-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Financial Information</h3>
        </div>
        {collapsedSections.financial ? 
          <ChevronRight className="h-5 w-5 text-gray-400" /> : 
          <ChevronDown className="h-5 w-5 text-gray-400" />
        }
      </button>
      
      {!collapsedSections.financial && (
        <div className="px-4 pb-4 border-t border-gray-100">
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div>
              <label className="text-sm font-medium text-gray-500">Claim Amount</label>
              <p className="text-lg font-semibold text-gray-900">
                {formatAmount(application.financial?.claimAmount)}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Approved Amount</label>
              <p className="text-lg font-semibold text-green-600">
                {formatAmount(application.financial?.approvedAmount)}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Co-pay</label>
              <p className="text-gray-900">
                {application.financial?.copayPercentage}%
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Deductible</label>
              <p className="text-gray-900">
                {formatAmount(application.financial?.deductible)}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>

    {/* Discharge / Dispatch Documents Section (supports array-based storage & legacy key-based) */}
    {(() => {
      const dischargeArray = Array.isArray(application?.dischargeDocuments) ? application.dischargeDocuments : [];
      const dispatchArray = Array.isArray(application?.dispatchDocuments) ? application.dispatchDocuments : [];
      const keyBased = application?.documents 
        ? Object.entries(application.documents).filter(([key]) => key.toLowerCase().includes('discharge'))
        : [];
      const hasAny = dischargeArray.length || dispatchArray.length || keyBased.length;
      if (!hasAny) return null;
      return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <button
            onClick={() => onToggleSection('dischargeDocs')}
            className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center space-x-3">
              <div className="bg-teal-100 p-2 rounded-lg">
                <FileText className="h-4 w-4 text-teal-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                Discharge / Dispatch Documents
                <span className="ml-2 text-sm font-normal text-gray-500">({dischargeArray.length + dispatchArray.length + keyBased.length})</span>
              </h3>
            </div>
            {collapsedSections.dischargeDocs ? (
              <ChevronRight className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            )}
          </button>
          {!collapsedSections.dischargeDocs && (
            <div className="px-4 pb-4 border-t border-gray-100">
              {/* Array based Discharge docs */}
              {dischargeArray.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-xs font-semibold text-teal-700 uppercase tracking-wide mb-2">Discharge Documents</h4>
                  <div className="space-y-2">
                    {dischargeArray.map((doc, idx) => (
                      <div
                        key={doc.id || doc.url || idx}
                        className="flex items-center justify-between p-2 bg-white rounded border border-teal-200 hover:bg-teal-50 transition-colors"
                      >
                        <div className="flex flex-col flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <FileText className="h-4 w-4 text-teal-500 flex-shrink-0" />
                            <span className="text-sm text-gray-700 truncate">
                              {doc.title || doc.fileName || doc.name || doc.originalName || `Document ${idx + 1}`}
                            </span>
                            {documentQueries[`discharge_${doc.id || idx}`] && documentQueries[`discharge_${doc.id || idx}`].length > 0 && (
                              <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-0.5 rounded-full">
                                {documentQueries[`discharge_${doc.id || idx}`].filter(q => q.status === 'open').length}
                              </span>
                            )}
                          </div>
                          {(doc.description || doc.remarks) && (
                            <p className="text-xs text-gray-500 mt-0.5 truncate">{doc.description || doc.remarks}</p>
                          )}
                        </div>
                        <div className="flex items-center space-x-1 ml-2">
                          {onOpenQueryModal && (
                            <button
                              onClick={() => onOpenQueryModal(`discharge_${doc.id || idx}`)}
                              className="text-orange-600 hover:text-orange-800 p-1"
                              title="Raise Query"
                            >
                              <HelpCircle className="h-3 w-3" />
                            </button>
                          )}
                          {doc.url && (
                            <button
                              onClick={() => onOpenDocument(doc)}
                              className="text-teal-600 hover:text-teal-800 p-1"
                              title="View Document"
                            >
                              <Eye className="h-3 w-3" />
                            </button>
                          )}
                          {doc.url && (
                            <button
                              onClick={() => downloadFile(doc.url, doc.fileName || doc.name || doc.title)}
                              className="text-green-600 hover:text-green-800 p-1"
                              title="Download Document"
                            >
                              <Download className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Array based Dispatch docs */}
              {dispatchArray.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-xs font-semibold text-teal-700 uppercase tracking-wide mb-2">Dispatch Documents</h4>
                  <div className="space-y-2">
                    {dispatchArray.map((doc, idx) => (
                      <div
                        key={doc.id || doc.url || `dispatch_${idx}`}
                        className="flex items-center justify-between p-2 bg-white rounded border border-teal-200 hover:bg-teal-50 transition-colors"
                      >
                        <div className="flex flex-col flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <FileText className="h-4 w-4 text-teal-500 flex-shrink-0" />
                            <span className="text-sm text-gray-700 truncate">
                              {doc.title || doc.fileName || doc.name || doc.originalName || `Dispatch Doc ${idx + 1}`}
                            </span>
                            {documentQueries[`dispatch_${doc.id || idx}`] && documentQueries[`dispatch_${doc.id || idx}`].length > 0 && (
                              <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-0.5 rounded-full">
                                {documentQueries[`dispatch_${doc.id || idx}`].filter(q => q.status === 'open').length}
                              </span>
                            )}
                          </div>
                          {(doc.description || doc.remarks) && (
                            <p className="text-xs text-gray-500 mt-0.5 truncate">{doc.description || doc.remarks}</p>
                          )}
                        </div>
                        <div className="flex items-center space-x-1 ml-2">
                          {onOpenQueryModal && (
                            <button
                              onClick={() => onOpenQueryModal(`dispatch_${doc.id || idx}`)}
                              className="text-orange-600 hover:text-orange-800 p-1"
                              title="Raise Query"
                            >
                              <HelpCircle className="h-3 w-3" />
                            </button>
                          )}
                          {doc.url && (
                            <button
                              onClick={() => onOpenDocument(doc)}
                              className="text-teal-600 hover:text-teal-800 p-1"
                              title="View Document"
                            >
                              <Eye className="h-3 w-3" />
                            </button>
                          )}
                          {doc.url && (
                            <button
                              onClick={() => downloadFile(doc.url, doc.fileName || doc.name || doc.title)}
                              className="text-green-600 hover:text-green-800 p-1"
                              title="Download Document"
                            >
                              <Download className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Legacy key-based discharge docs (for backward compatibility) */}
              {keyBased.length > 0 && (
                <div className={`mt-6 ${dischargeArray.length === 0 ? 'mt-4' : ''}`}>
                  {(dischargeArray.length > 0 || dispatchArray.length > 0) && (
                    <h4 className="text-xs font-semibold text-teal-700 uppercase tracking-wide mb-2">Legacy (Key-Based)</h4>
                  )}
                  <div className="space-y-2">
                    {keyBased.map(([key, doc]) => (
                      <div
                        key={key}
                        className="flex items-center justify-between p-2 bg-white rounded border border-teal-200 hover:bg-teal-50 transition-colors"
                      >
                        <div className="flex items-center space-x-2">
                          <FileText className="h-4 w-4 text-teal-500" />
                          <span className="text-sm text-gray-700 truncate">
                            {key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())}
                          </span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <button
                            onClick={() => onOpenDocument(doc)}
                            className="text-teal-600 hover:text-teal-800 p-1"
                            title="View Document"
                          >
                            <Eye className="h-3 w-3" />
                          </button>
                          <button
                            onClick={() => downloadFile(doc.url, doc.fileName || doc.name)}
                            className="text-green-600 hover:text-green-800 p-1"
                            title="Download Document"
                          >
                            <Download className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {!dischargeArray.length && !dispatchArray.length && !keyBased.length && (
                <div className="mt-4 text-sm text-gray-500">No discharge or dispatch documents uploaded.</div>
              )}
            </div>
          )}
        </div>
      );
    })()}

    {/* Documents Section */}
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      <button
        onClick={() => onToggleSection('documents')}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center space-x-3">
          <div className="bg-blue-100 p-2 rounded-lg">
            <FileText className="h-4 w-4 text-blue-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Documents</h3>
        </div>
        {collapsedSections.documents ? 
          <ChevronRight className="h-5 w-5 text-gray-400" /> : 
          <ChevronDown className="h-5 w-5 text-gray-400" />
        }
      </button>
      
      {!collapsedSections.documents && (
        <div className="px-4 pb-4 border-t border-gray-100">
          {application.documents && Object.keys(application.documents).length > 0 ? (
            <div className="space-y-3 mt-4">
              {/* Policy Documents */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Policy Documents</h4>
                <div className="space-y-2">
                  {Object.entries(application.documents)
                    .filter(([key]) => key.toLowerCase().includes('policy') || 
                                      key.toLowerCase().includes('insurance') ||
                                      key.toLowerCase().includes('id') ||
                                      key.toLowerCase().includes('card'))
                    .map(([key, doc]) => (
                    <div
                      key={key}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center space-x-3 flex-1">
                        <FileText className="h-4 w-4 text-blue-500" />
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <h5 className="font-medium text-gray-900 text-sm">
                              {key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())}
                            </h5>
                            {documentQueries[key] && documentQueries[key].length > 0 && (
                              <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-0.5 rounded-full">
                                {documentQueries[key].filter(q => q.status === 'open').length} queries
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-600">{doc.fileName}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {onOpenQueryModal && (
                          <button
                            onClick={() => onOpenQueryModal(key)}
                            className="text-orange-600 hover:text-orange-800 p-1"
                            title="Raise Query"
                          >
                            <HelpCircle className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => onOpenDocument(doc)}
                          className="text-blue-600 hover:text-blue-800 p-1"
                          title="View Document"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            downloadFile(doc.url, doc.fileName || doc.name);
                          }}
                          className="text-green-600 hover:text-green-800 p-1"
                          title="Download Document"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Medical Documents */}
              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Medical Documents</h4>
                <div className="space-y-2">
                  {Object.entries(application.documents)
                    .filter(([key]) => !(key.toLowerCase().includes('policy') || 
                                        key.toLowerCase().includes('insurance') ||
                                        key.toLowerCase().includes('id') ||
                                        key.toLowerCase().includes('card') ||
                                        key.toLowerCase().includes('discharge')))
                    .map(([key, doc]) => (
                    <div
                      key={key}
                      className="flex items-center justify-between p-2 bg-white rounded border border-gray-200 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center space-x-2 flex-1">
                        <FileText className="h-4 w-4 text-gray-500" />
                        <div className="flex items-center space-x-2 flex-1">
                          <span className="text-sm text-gray-700">
                            {key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())}
                          </span>
                          {documentQueries[key] && documentQueries[key].length > 0 && (
                            <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-0.5 rounded-full">
                              {documentQueries[key].filter(q => q.status === 'open').length}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-1">
                        {onOpenQueryModal && (
                          <button
                            onClick={() => onOpenQueryModal(key)}
                            className="text-orange-600 hover:text-orange-800 p-1"
                            title="Raise Query"
                          >
                            <HelpCircle className="h-3 w-3" />
                          </button>
                        )}
                        <button
                          onClick={() => onOpenDocument(doc)}
                          className="text-blue-600 hover:text-blue-800 p-1"
                          title="View Document"
                        >
                          <Eye className="h-3 w-3" />
                        </button>
                        <button
                          onClick={() => {
                            downloadFile(doc.url, doc.fileName || doc.name);
                          }}
                          className="text-green-600 hover:text-green-800 p-1"
                          title="Download Document"
                        >
                          <Download className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          ) : (
            <div className="text-center py-8 mt-4">
              <FileText className="h-8 w-8 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-600">No documents available</p>
            </div>
          )}
        </div>
      )}
    </div>
  </div>
);

export default ApplicationRightPanel;
